/*
 * The customer lifecycle: the stage a customer is at, set per customer on the Intel tab (stored on their profile,
 * and carried on their Health check for the customer table) and shown on the Briefs tab and in the customer table.
 * Serve & upsell is one stage (stored as upsell) that spans the Serve and Upsell columns of the Briefs tab's board.
 */

export type LifecycleStage = 'trial' | 'onboarding' | 'upsell' | 'retention' | 'expired'

/* the Briefs tab's lifecycle board: its columns (Expired is only shown for an expired customer) */
export type BoardColumn = 'trial' | 'onboarding' | 'serve' | 'upsell' | 'retention' | 'expired'

export const BOARD_COLUMNS: { value: BoardColumn; label: string }[] = [
  { value: 'trial', label: 'Trial' },
  { value: 'onboarding', label: 'Onboarding' },
  { value: 'serve', label: 'Serve' },
  { value: 'upsell', label: 'Upsell' },
  { value: 'retention', label: 'Retention' },
  { value: 'expired', label: 'Expired' },
]

export const LIFECYCLE_STAGES: { value: LifecycleStage; label: string; columns: BoardColumn[] }[] = [
  { value: 'trial', label: 'Trial', columns: ['trial'] },
  { value: 'onboarding', label: 'Onboarding', columns: ['onboarding'] },
  { value: 'upsell', label: 'Serve & upsell', columns: ['serve', 'upsell'] },
  { value: 'retention', label: 'Retention', columns: ['retention'] },
  { value: 'expired', label: 'Expired', columns: ['expired'] },
]

/* a customer whose stage hasn't been set is taken to be at Serve & upsell */
export const DEFAULT_LIFECYCLE_STAGE: LifecycleStage = 'upsell'

export const isLifecycleStage = (value: unknown): value is LifecycleStage =>
  LIFECYCLE_STAGES.some(stage => stage.value === value)

export const lifecycleStageFor = (value?: string) => LIFECYCLE_STAGES.find(stage => stage.value === value)

/* the stage to show and save: Serve & upsell when none is set, or for a value that's no longer a stage (e.g. serve) */
export const stageOrDefault = (value?: string): LifecycleStage =>
  isLifecycleStage(value) ? value : DEFAULT_LIFECYCLE_STAGE

/* a stage's place in the lifecycle (for sorting); undefined when it isn't set */
export const lifecycleRank = (value?: string): number | undefined => {
  const index = LIFECYCLE_STAGES.findIndex(stage => stage.value === value)
  return index < 0 ? undefined : index
}
